import clsx from 'clsx'
import { Button } from 'components/Button/Button'
import { ReactComponent as MissionSvg } from 'images/icons/mission_play.svg'
import { writeCrossTabCatalogModeBusy } from 'pages/Robot/RobotView/RobotLeftBlock/Service/catalogServiceCrossTab'
import {
  setCatalogOperationTimeoutNotice,
  useCatalogOperationTimeoutActionKey,
} from 'pages/Robot/RobotView/RobotLeftBlock/Service/catalogOperationTimeoutNotice'
import { serviceModeActionKey } from 'pages/Robot/RobotView/RobotLeftBlock/Service/serviceModeActionKeys'
import RobotModeIcons from 'pages/Assets/robots/RobotItem/RobotModeIcons'
import { useEffect, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { useDispatch, useSelector } from 'react-redux'
import { generatePath, useNavigate } from 'react-router-dom'
import { RobotActions } from 'store/actions/robot'
import { selectRobot } from 'store/selectors/robot'
import store from 'store/store'
import { robotThunks } from 'store/thunks/robot'
import { resolveCatalogOperationPresentation } from 'utils/catalogServicePresentation'
import { CATALOG_OPERATION_TIMEOUT_MS } from 'utils/catalogOperationSettlement'
import { PATH, ROBOT_MODES, ROBOT_NETWORK_STATUSES, TASK_RUNTIME_STATE } from 'utils/constants'
import { getTaskRuntimeState, hasActiveTaskRuntimeState } from 'utils/taskRuntimeState'
import type { RootState } from 'utils/interfaces'
import { VENDOR } from 'utils/vendorDisplayName'

import style from './RobotMissionMainStatus.module.css'

enum STATES {
  SELECT_MISSION,
  MISSION_READY_TO_START,
  UPLOADING_MISSION_ERROR,
  UPLOADING,
  CALCULATING_SCENARIO,
  TURN_ON_ROBOT,
  MISSION_IS_ACTIVE,
  MISSION_IS_NOT_ACTIVE,
}

const RobotMissionMainStatus = () => {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const dispatch = useDispatch()
  const robot = useSelector(selectRobot)
  const activeMode = robot.robotInfo?.activeMode
  const isOnline = robot.robotInfo?.networkStatus === ROBOT_NETWORK_STATUSES.ONLINE
  const statusCancelKey = serviceModeActionKey.catalogServiceStatusCancel()
  const statusCancelLoading = useSelector(
    (state: RootState) => state.robot.serviceModeSwitchOptimistic?.actionKey === statusCancelKey
  )
  const statusCancelTimedOut = useCatalogOperationTimeoutActionKey(robot.robotInfo?.id) === statusCancelKey
  const modeServicePoint = activeMode === ROBOT_MODES.MODE_SERVICE_POINT

  useEffect(() => {
    if (!statusCancelLoading || !robot.robotInfo?.id) {
      return
    }
    const robotId = robot.robotInfo.id
    const timer = window.setTimeout(() => {
      setCatalogOperationTimeoutNotice(robotId, statusCancelKey, activeMode ?? null)
      writeCrossTabCatalogModeBusy(robotId, null)
      dispatch(RobotActions.clearServiceModeSwitchOptimistic())
    }, CATALOG_OPERATION_TIMEOUT_MS)
    return () => window.clearTimeout(timer)
  }, [activeMode, dispatch, robot.robotInfo?.id, statusCancelKey, statusCancelLoading])

  useEffect(() => {
    if (!statusCancelLoading || modeServicePoint) {
      return
    }
    const robotId = robot.robotInfo?.id
    if (robotId) {
      writeCrossTabCatalogModeBusy(robotId, null)
    }
    dispatch(RobotActions.clearServiceModeSwitchOptimistic())
  }, [dispatch, modeServicePoint, robot.robotInfo?.id, statusCancelLoading])
  const isGausium = robot.robotInfo?.vendor === VENDOR.GAUSIUM
  const isAsbis = robot.robotInfo?.vendor === VENDOR.ASBIS
  const taskRuntimeState = getTaskRuntimeState(robot.robotInfo)

  const servicePointPresentation = resolveCatalogOperationPresentation(robot.robotInfo)

  const state = useMemo(() => {
    if (activeMode === null || activeMode === undefined) {
      return STATES.TURN_ON_ROBOT
    }

    if (isGausium) {
      return hasActiveTaskRuntimeState(robot.robotInfo) ? STATES.MISSION_IS_ACTIVE : STATES.MISSION_IS_NOT_ACTIVE
    }

    if (servicePointPresentation) {
      return STATES.MISSION_IS_ACTIVE
    }

    if (activeMode !== ROBOT_MODES.MODE_NOT_SET) {
      return STATES.MISSION_IS_ACTIVE
    }

    if (activeMode === ROBOT_MODES.MODE_NOT_SET) {
      return STATES.MISSION_IS_NOT_ACTIVE
    }

    return STATES.TURN_ON_ROBOT
  }, [activeMode, servicePointPresentation, isGausium, robot.robotInfo])

  const styles = {
    [`${STATES.MISSION_IS_ACTIVE}`]: style.work,
    [`${STATES.MISSION_IS_NOT_ACTIVE}`]: style.work,
  }

  const messages = {
    [STATES.TURN_ON_ROBOT]: t('robot.turn_on'),
    [STATES.MISSION_IS_ACTIVE]: servicePointPresentation
      ? servicePointPresentation.progressLabel
      : activeMode === ROBOT_MODES.MODE_MAPPING
      ? 'Mapping in progress'
      : activeMode === ROBOT_MODES.MODE_ROUTE_RECORDING
      ? 'Route recording in progress'
      : activeMode === ROBOT_MODES.MODE_MANUAL
      ? 'The robot in the manual mode'
      : activeMode === ROBOT_MODES.MODE_SWITCHING_TO_LIDAR_LOCALIZATION ||
        activeMode === ROBOT_MODES.MODE_SWITCHING_TO_GNSS_BASED_LOCALIZATION ||
        activeMode === ROBOT_MODES.MODE_SWITCHING_TO_COMBINED_LOCALIZATION
      ? 'Switching localization mode'
      : activeMode === ROBOT_MODES.MODE_REBOOT_ROBOT
      ? 'Robot is rebooting'
      : activeMode === ROBOT_MODES.MODE_UPDATING_FIRMWARE
      ? 'Updating the robot firmware'
      : activeMode === ROBOT_MODES.MODE_AUTOMATIC
      ? isGausium
        ? taskRuntimeState === TASK_RUNTIME_STATE.PAUSED
          ? t('robot.task_paused')
          : taskRuntimeState === TASK_RUNTIME_STATE.IDLE
          ? t('robot.not_active')
          : t('robot.executing_task')
        : t('robot.active')
      : isGausium
      ? taskRuntimeState === TASK_RUNTIME_STATE.PAUSED
        ? t('robot.task_paused')
        : taskRuntimeState === TASK_RUNTIME_STATE.RUNNING
        ? t('robot.executing_task')
        : t('robot.not_active')
      : 'Unknown mode',
    [STATES.MISSION_IS_NOT_ACTIVE]:
      isGausium && taskRuntimeState === TASK_RUNTIME_STATE.PAUSED ? t('robot.task_paused') : t('robot.not_active'),
  }

  const isGoToMissionEnabled =
    !isAsbis && (isGausium ? hasActiveTaskRuntimeState(robot.robotInfo) : activeMode === ROBOT_MODES.MODE_AUTOMATIC)

  const onGoToMissionClick = () => {
    if (robot) {
      const path = generatePath(PATH.MISSION_CONTROL, { id: String(robot.robotInfo?.id) })
      navigate(path)
    }
  }

  if (!isOnline) {
    return null
  }

  return (
    <div>
      <div className={clsx(style.main)}>
        <div className={style.iconsWrapper}>
          {robot.robotInfo && (
            <RobotModeIcons
              robot={robot.robotInfo}
              large
              hideEmergency
              hidePause
            />
          )}
        </div>
        <div className={style.message} dangerouslySetInnerHTML={{ __html: messages[state] }} />
        <div className={style.buttonWrapper}>
          {modeServicePoint && robot.robotInfo?.id != null && (
            <Button.Standart
              className={style.buttonWide}
              loading={statusCancelLoading}
              disabled={statusCancelLoading || !isOnline}
              title={statusCancelTimedOut ? 'No confirmation' : isOnline ? 'Cancel service' : 'Robot is offline'}
              onClick={() => {
                store.dispatch(
                  robotThunks.setRobotNotSetMode(robot.robotInfo!.id, { actionKey: statusCancelKey })
                )
              }}
            >
              Cancel service
            </Button.Standart>
          )}
          {isGoToMissionEnabled && (
            <Button.Standart className={style.buttonWide} onClick={onGoToMissionClick} disabled={!isGoToMissionEnabled}>
              {t('robot.button_go_to_mission')}
            </Button.Standart>
          )}
        </div>
      </div>
    </div>
  )
}

export default RobotMissionMainStatus
